import { HELPDESK_NOTE_MAX, HELPDESK_PRIORITIES, formatTicketNo, helpdeskNextStatuses } from "@seagames/contracts";
import { Field, Modal, ModalActions, Select } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { addHelpdeskNote, helpdeskTicketOptions, invalidateHelpdesk, patchHelpdeskTicket } from "../../lib/api/helpdesk";
import { errorMessage } from "../../lib/api/messages";
import { formatDateTime, formatShort } from "../../lib/format/datetime";
import { toast } from "../../lib/ui/toast";
import { PhoneLink, useCallingCodes } from "../transport-fleet";
import { CheckIcon } from "../transport/icons";
import { TicketStatusChip } from "./status";
import { useTicketPlace } from "./ticket-row";
import { type HelpdeskNote, type HelpdeskPriority, type HelpdeskStatus, type HelpdeskTicketDetail, canWorkTicket } from "./types";

/**
 * One ticket (her `tkSheet` open): where, contingent, contact, priority; the
 * notes timeline; then — for someone who may work it (`can_handle`, decided in
 * the API's SELECT) — Status + "Add a note" → Update ticket. A status change
 * rides on its note (one transaction, the trigger guards the graph); a
 * priority change is its own PATCH. Notes are append-only, so the timeline
 * is the ticket's history. A reporter (a NOC) reads the same sheet without
 * the form.
 */
export function TicketModal({ ticketId, onClose }: { ticketId: string; onClose: () => void }) {
	const { t } = useTranslation("helpdesk");
	const detail = useQuery(helpdeskTicketOptions(ticketId));
	const ticket = detail.data;
	// The form lives in the body; its buttons sit in the sheet's footer (always in view on a phone).
	const [form, setForm] = useState({ changed: false, saving: false });
	const working = !!ticket && canWorkTicket(ticket);

	return (
		<Modal
			open
			onClose={onClose}
			size="md"
			title={ticket ? ticket.title : t("ticket.loadingTitle")}
			description={ticket ? <TicketSubtitle ticket={ticket} /> : undefined}
			dismissible={!form.saving}
			footer={
				working ? (
					<ModalActions>
						<button type="button" className="btn btn-sm btn-line" onClick={onClose} disabled={form.saving}>
							{t("common.cancel")}
						</button>
						<button type="submit" form="hd-ticket-form" className="btn btn-sm btn-primary" disabled={!form.changed || form.saving}>
							{form.saving ? <span className="loading loading-spinner loading-xs" /> : <CheckIcon className="size-[0.9375rem]" />}
							{t("ticket.update")}
						</button>
					</ModalActions>
				) : (
					<ModalActions>
						<button type="button" className="btn btn-sm btn-line" onClick={onClose}>
							{t("ticket.close")}
						</button>
					</ModalActions>
				)
			}
		>
			{detail.isPending ? (
				<div className="grid gap-[0.5rem]" aria-busy>
					<div className="skeleton h-[8rem] rounded-[0.5rem]" />
					<div className="skeleton h-[3rem] rounded-[0.5rem]" />
				</div>
			) : detail.isError ? (
				<div role="alert" className="flex items-center justify-between gap-[1rem] rounded-field bg-gms-bad-soft px-[0.875rem] py-[0.75rem] text-[0.8125rem] text-gms-bad">
					<span>{errorMessage(detail.error)}</span>
					<button type="button" className="btn btn-xs btn-line" onClick={() => void detail.refetch()}>
						{t("common.retry")}
					</button>
				</div>
			) : ticket ? (
				<TicketBody ticket={ticket} onClose={onClose} onFormState={setForm} />
			) : null}
		</Modal>
	);
}

function TicketSubtitle({ ticket }: { ticket: HelpdeskTicketDetail }) {
	const { t } = useTranslation("helpdesk");
	return (
		<span>
			{formatTicketNo(ticket.ticket_no)}
			{ticket.created_by && (
				<>
					<span aria-hidden> · </span>
					{ticket.created_by.name}
				</>
			)}
			<span aria-hidden> · </span>
			<time dateTime={ticket.created_at} title={formatDateTime(ticket.created_at)}>
				{formatShort(ticket.created_at)}
			</time>
			<span aria-hidden> · </span>
			{t(`source.${ticket.source}`)}
		</span>
	);
}

function TicketBody({ ticket, onClose, onFormState }: { ticket: HelpdeskTicketDetail; onClose: () => void; onFormState: (s: { changed: boolean; saving: boolean }) => void }) {
	const { t } = useTranslation("helpdesk");
	const place = useTicketPlace();
	const callingCodes = useCallingCodes();
	const org = ticket.organization;
	const [status, setStatus] = useState<HelpdeskStatus>(ticket.status);
	const [priority, setPriority] = useState<HelpdeskPriority>(ticket.priority);
	const [note, setNote] = useState("");
	const [noteError, setNoteError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	// Someone else moved it while the sheet was open: start from what is true now.
	useEffect(() => {
		setStatus(ticket.status);
		setPriority(ticket.priority);
	}, [ticket.status, ticket.priority]);

	const statusOptions = [ticket.status, ...(ticket.next_statuses ?? helpdeskNextStatuses(ticket.status))].map((s) => ({ value: s, label: t(`status.label.${s}`) }));
	const trimmed = note.trim();
	const changed = status !== ticket.status || priority !== ticket.priority || trimmed !== "";
	useEffect(() => onFormState({ changed, saving }), [changed, saving, onFormState]);

	const save = async () => {
		if (trimmed.length > HELPDESK_NOTE_MAX) {
			setNoteError(t("validation.noteMax", { max: HELPDESK_NOTE_MAX }));
			return;
		}
		setSaving(true);
		try {
			if (priority !== ticket.priority) await patchHelpdeskTicket(ticket.helpdesk_ticket_id, { priority });
			// A status change may come with no text: the API stores a status-only entry (body null).
			if (status !== ticket.status || trimmed !== "")
				await addHelpdeskNote(ticket.helpdesk_ticket_id, { ...(trimmed !== "" ? { body: trimmed } : {}), ...(status !== ticket.status ? { status } : {}) });
			await invalidateHelpdesk();
			toast.success(t("ticket.updated", { ticket: formatTicketNo(ticket.ticket_no) }));
			onClose();
		} catch (err) {
			toast.error(err, t("ticket.updateFailed"));
			setSaving(false);
		}
	};

	const rows: Array<[string, ReactNode]> = [
		[t("ticket.where"), place(ticket)],
		[t("ticket.contingent"), org ? (org.code ? t("ticket.orgCodeName", { code: org.code, name: org.name }) : org.name) : "—"],
		[
			t("ticket.contact"),
			ticket.contact_name || ticket.contact_phone ? (
				<span className="inline-flex flex-wrap items-center gap-x-[0.5rem]">
					{ticket.contact_name && <span>{ticket.contact_name}</span>}
					{ticket.contact_phone && <PhoneLink phone={ticket.contact_phone} callingCodes={callingCodes} />}
				</span>
			) : (
				"—"
			),
		],
		[t("ticket.priority"), t(`priority.label.${ticket.priority}`)],
		[t("ticket.status"), <TicketStatusChip key="st" status={ticket.status} />],
	];
	if (ticket.assignee) rows.push([t("ticket.assignee"), ticket.assignee.name]);

	return (
		<div className="grid gap-[1rem]">
			{/* Her `kv`: label left, value right, hairlines between. */}
			<dl className="grid rounded-[0.625rem] border border-gms-line text-[0.8125rem]">
				{rows.map(([label, value], i) => (
					<div key={label} className={`grid grid-cols-[7.5rem_minmax(0,1fr)] gap-[0.75rem] px-[0.875rem] py-[0.5625rem] ${i > 0 ? "border-t border-gms-line" : ""}`}>
						<dt className="text-gms-ink-3">{label}</dt>
						<dd className="min-w-0 font-medium break-words text-gms-navy-ink">{value}</dd>
					</div>
				))}
			</dl>

			{ticket.details && (
				<div className="grid gap-[0.25rem]">
					<h3 className="text-[0.75rem] font-semibold text-gms-ink-3">{t("ticket.details")}</h3>
					<p className="rounded-[0.5rem] bg-gms-soft px-[0.75rem] py-[0.5rem] text-[0.8125rem] leading-[1.5] whitespace-pre-line text-gms-ink-2">{ticket.details}</p>
				</div>
			)}

			<Timeline notes={ticket.notes} />

			{canWorkTicket(ticket) && (
				<form
					id="hd-ticket-form"
					className="grid gap-[0.875rem]"
					onSubmit={(e) => {
						e.preventDefault();
						if (changed && !saving) void save();
					}}
				>
					<div className="grid gap-[0.75rem] sm:grid-cols-2">
						<Field label={t("ticket.status")} htmlFor="hd-status">
							<Select id="hd-status" searchable={false} options={statusOptions} value={status} onChange={(v) => v && setStatus(v as HelpdeskStatus)} disabled={saving} />
						</Field>
						<Field label={t("ticket.priority")} htmlFor="hd-priority">
							<Select
								id="hd-priority"
								searchable={false}
								options={HELPDESK_PRIORITIES.map((p) => ({ value: p, label: t(`priority.label.${p}`) }))}
								value={priority}
								onChange={(v) => v && setPriority(v as HelpdeskPriority)}
								disabled={saving}
							/>
						</Field>
					</div>
					<Field label={t("ticket.addNote")} htmlFor="hd-note" error={noteError ?? undefined} aside={t("common.optional")}>
						<textarea
							id="hd-note"
							rows={3}
							value={note}
							maxLength={HELPDESK_NOTE_MAX}
							onChange={(e) => {
								setNote(e.target.value);
								setNoteError(null);
							}}
							placeholder={t("ticket.notePlaceholder")}
							className={`textarea w-full ${noteError ? "textarea-error" : ""}`}
							disabled={saving}
						/>
					</Field>
				</form>
			)}
		</div>
	);
}

/** The notes, oldest first (her `.eskip` list): time, what was said or done, by whom. A status change reads "Status: In progress." before its note. */
function Timeline({ notes }: { notes: HelpdeskNote[] }) {
	const { t } = useTranslation("helpdesk");
	if (notes.length === 0) return <p className="text-[0.8125rem] text-gms-ink-3">{t("ticket.noNotes")}</p>;
	return (
		<section className="grid gap-[0.375rem]">
			<h3 className="text-[0.75rem] font-semibold text-gms-ink-3">{t("ticket.timeline")}</h3>
			<ol className="grid rounded-[0.625rem] border border-gms-line">
				{notes.map((n, i) => (
					<li key={n.helpdesk_ticket_note_id} className={`grid grid-cols-[6.5rem_minmax(0,1fr)] gap-[0.75rem] px-[0.875rem] py-[0.5rem] text-[0.8125rem] ${i > 0 ? "border-t border-gms-line" : ""}`}>
						<time dateTime={n.created_at} title={formatDateTime(n.created_at)} className="text-[0.75rem] text-gms-ink-3 tabular-nums">
							{formatShort(n.created_at)}
						</time>
						<span className="min-w-0 break-words text-gms-ink-2">
							{n.status_to && <b className="font-semibold text-gms-navy-ink">{t("ticket.statusNote", { status: t(`status.label.${n.status_to}`) })} </b>}
							{n.body !== null && <span className="whitespace-pre-line">{n.body}</span>}
							{n.created_by && <span className="text-gms-ink-3"> · {n.created_by.name}</span>}
						</span>
					</li>
				))}
			</ol>
		</section>
	);
}
